/** Türkiye adm1 viewBox genişliği — ilçe haritası aynı ekran oranı için buna göre ölçeklenir */
export const NATIONAL_MAP_VIEWBOX_WIDTH = 1436;
export const PROVINCE_LABEL_FONT_SIZE = 10;
const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * İlçe SVG’si dar viewBox kullandığında sabit 10 birim ekranda dev görünmesin diye:
 * görünür boyut ≈ il haritasındaki ile aynı kalacak şekilde user-space punto.
 * @param {SVGSVGElement} svg
 */
export function districtLabelFontSizeForSvg(svg) {
  let vbW = NATIONAL_MAP_VIEWBOX_WIDTH;
  try {
    const b = svg.viewBox?.baseVal;
    if (b && b.width > 0) {
      vbW = b.width;
    } else {
      const parts = svg.getAttribute('viewBox')?.trim().split(/[\s,]+/).map(Number);
      if (parts?.length >= 3 && Number.isFinite(parts[2]) && parts[2] > 0) {
        vbW = parts[2];
      }
    }
  } catch {
    /* ignore */
  }
  const raw = PROVINCE_LABEL_FONT_SIZE * (vbW / NATIONAL_MAP_VIEWBOX_WIDTH);
  const clamped = Math.max(2.5, Math.min(PROVINCE_LABEL_FONT_SIZE, raw));
  return Math.round(clamped * 100) / 100;
}

/**
 * @typedef {Object} FeatureLabelOptions
 * @property {string} [featuresSelector]
 * @property {string} labelLayerId
 * @property {Record<string, { x: number, y: number, scale?: number, rotate?: number }>} [positionOverrides]
 * @property {boolean} [draggable]
 * @property {string} [dragDataAttr] — örn. data-province-id
 * @property {string} [labelBaseClass] — varsayılan province-label (CSS ile uyumlu)
 * @property {number} [fontSize] — viewBox birimi; verilmezse il punto kullanılır
 * @property {number} [globalFontScale] — ilçe vb. tüm etiket punto çarpanı (varsayılan 1)
 * @property {string | null} [editorSelectedId] — editörde vurgu sınıfı
 */

/**
 * @param {SVGSVGElement} svg
 * @param {FeatureLabelOptions} options
 */
export function addFeatureGroupLabels(svg, options) {
  const {
    featuresSelector = '#features > g[id]',
    labelLayerId,
    positionOverrides = {},
    draggable = false,
    dragDataAttr = 'data-province-id',
    labelBaseClass = 'province-label',
    fontSize = PROVINCE_LABEL_FONT_SIZE,
    globalFontScale = 1,
    editorSelectedId = null,
  } = options;

  const gfs =
    typeof globalFontScale === 'number' && Number.isFinite(globalFontScale) && globalFontScale > 0
      ? Math.min(4, Math.max(0.25, globalFontScale))
      : 1;

  const existing = svg.querySelector(`#${labelLayerId}`);
  if (existing) existing.remove();

  const labelsLayer = document.createElementNS(SVG_NS, 'g');
  labelsLayer.setAttribute('id', labelLayerId);
  labelsLayer.setAttribute('pointer-events', draggable ? 'auto' : 'none');

  const groups = Array.from(svg.querySelectorAll(featuresSelector));

  groups.forEach((g) => {
    const rawTitle = g.getAttribute('title');
    const id = g.getAttribute('id');
    if (!rawTitle?.trim() || !id) return;

    let box;
    try {
      box = g.getBBox();
    } catch {
      return;
    }
    if (!box || box.width < 2 || box.height < 2) return;

    const cx = box.x + box.width / 2;
    const cy = box.y + box.height / 2;
    const label = rawTitle.trim().toLocaleUpperCase('tr-TR');

    const saved = positionOverrides[id];
    const x =
      saved && typeof saved.x === 'number' && Number.isFinite(saved.x) && typeof saved.y === 'number'
        ? saved.x
        : cx;
    const y = saved && typeof saved.y === 'number' && Number.isFinite(saved.y) ? saved.y : cy;
    const scaleMul =
      saved && typeof saved.scale === 'number' && Number.isFinite(saved.scale) && saved.scale > 0
        ? Math.min(4, Math.max(0.25, saved.scale))
        : 1;
    const rotateDeg =
      saved && typeof saved.rotate === 'number' && Number.isFinite(saved.rotate) ? saved.rotate : 0;
    const fs = Math.round(fontSize * scaleMul * gfs * 1000) / 1000;

    const text = document.createElementNS(SVG_NS, 'text');
    text.setAttribute('data-map-label-fs-base', String(fs));
    text.setAttribute('x', String(x));
    text.setAttribute('y', String(y));
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('dominant-baseline', 'central');
    text.setAttribute('font-size', String(fs));
    text.setAttribute('data-editor-rotate', String(rotateDeg));
    if (rotateDeg !== 0) {
      text.setAttribute('transform', `rotate(${rotateDeg} ${x} ${y})`);
    }
    let cls = labelBaseClass;
    if (draggable) cls += ` ${labelBaseClass}--draggable`;
    if (editorSelectedId && id === editorSelectedId) cls += ` ${labelBaseClass}--editor-selected`;
    text.setAttribute('class', cls);
    if (draggable) {
      text.setAttribute(dragDataAttr, id);
    }
    text.textContent = label;
    labelsLayer.appendChild(text);
  });

  svg.appendChild(labelsLayer);
}

/**
 * @param {SVGSVGElement} svg
 * @param {{
 *   positionOverrides?: Record<string, { x: number, y: number, scale?: number, rotate?: number }>,
 *   draggable?: boolean,
 *   editorSelectedId?: string | null,
 * }} [options]
 */
export function addProvinceLabels(svg, options = {}) {
  const { positionOverrides = {}, draggable = false, editorSelectedId = null } = options;

  addFeatureGroupLabels(svg, {
    labelLayerId: 'province-labels',
    positionOverrides,
    draggable,
    dragDataAttr: 'data-province-id',
    editorSelectedId,
  });
}

/**
 * İlçe haritası — varsayılan bbox merkezi; kalıcı kayıtlar API'den gelen overrides ile.
 * @param {SVGSVGElement} svg
 * @param {{
 *   positionOverrides?: Record<string, { x: number, y: number, scale?: number, rotate?: number }>,
 *   draggable?: boolean,
 *   editorSelectedId?: string | null,
 *   globalFontScale?: number
 * }} [options]
 */
export function addDistrictLabels(svg, options = {}) {
  const {
    positionOverrides = {},
    draggable = false,
    editorSelectedId = null,
    globalFontScale = 1,
  } = options;

  addFeatureGroupLabels(svg, {
    labelLayerId: 'district-labels',
    positionOverrides,
    draggable,
    dragDataAttr: 'data-district-id',
    fontSize: districtLabelFontSizeForSvg(svg),
    globalFontScale,
    editorSelectedId,
  });
}
